import type { ParsedSnapshot } from './parser.js';

/** Configurable error-rate bands (defaults documented; FR-TS-092). */
export interface ErrorThresholds {
  readonly green: number;
  readonly amber: number;
}
export const DEFAULT_THRESHOLDS: ErrorThresholds = { green: 0.01, amber: 0.05 };

export type ErrorBand = 'green' | 'amber' | 'red';

export function errorBand(rate: number, t: ErrorThresholds = DEFAULT_THRESHOLDS): ErrorBand {
  if (rate < t.green) return 'green';
  if (rate < t.amber) return 'amber';
  return 'red';
}

export interface HealthDisplay {
  readonly cls: string;
  readonly icon: string;
  readonly label: string;
}
export function healthDisplay(h: number): HealthDisplay {
  switch (h) {
    case 1:
      return { cls: 'degraded', icon: '!', label: 'degraded' };
    case 2:
      return { cls: 'unreachable', icon: 'X', label: 'unreachable' };
    default:
      return { cls: 'healthy', icon: 'OK', label: 'healthy' };
  }
}

export interface BreakerDisplay {
  readonly cls: string;
  readonly label: string;
}
export function breakerDisplay(b: number): BreakerDisplay {
  switch (b) {
    case 1:
      return { cls: 'throttling', label: 'THROTTLING' };
    case 2:
      return { cls: 'halted', label: 'HALTED' };
    default:
      return { cls: 'armed', label: 'ARMED' };
  }
}

export interface EdgeStyle {
  readonly width: number;
  readonly band: ErrorBand;
  readonly dashed: boolean;
}
/** Throughput -> stroke weight; error -> colour band AND a non-colour cue (dashed when not green). */
export function edgeStyle(throughputRps: number, errorRate: number, t: ErrorThresholds = DEFAULT_THRESHOLDS): EdgeStyle {
  const width = Math.max(1, Math.min(10, 1 + throughputRps / 3));
  const band = errorBand(errorRate, t);
  return { width, band, dashed: band !== 'green' };
}

export interface RenderOptions {
  readonly thresholds?: ErrorThresholds;
}

/** Minimal shape consumed by updateRailHints — avoids importing all of feed.ts. */
export interface FeedHint {
  bank: string;
  scenario: string;
  description: string;
}

const MONEY_SCENARIOS = new Set(['sct', 'sct_inst', 'internal_transfer', 'fx_convert', 'p2p_redeem']);
const EUR_HINT_RE = /€([\d,.]+)/;

/** Colour topology packets by the dominant money-rail and show last amount near each edge. */
export function updateRailHints(doc: Document, events: FeedHint[]): void {
  for (const bankId of ['evaris_es', 'evairi_com']) {
    const latest = events.find((e) => e.bank === bankId && MONEY_SCENARIOS.has(e.scenario));
    if (!latest) continue;

    const m = EUR_HINT_RE.exec(latest.description);
    const amtEl = doc.querySelector(`[data-amount-label="${bankId}"]`);
    if (amtEl) amtEl.textContent = m ? `€${m[1]}` : '';

    const rail =
      latest.scenario === 'fx_convert' ? 'fx'
      : latest.scenario === 'sct_inst' ? 'inst'
      : latest.scenario === 'sct' ? 'sct'
      : latest.scenario === 'internal_transfer' ? 'internal'
      : 'p2p';
    const flowG = doc.querySelector(`[data-flow="${bankId}"]`);
    if (flowG) flowG.setAttribute('data-rail', rail);
  }
}

function setText(parent: Element | null, field: string, txt: string): void {
  if (!parent) return;
  const el = parent.querySelector(`[data-field="${field}"]`);
  if (el) el.textContent = txt;
}

function setAttr(el: Element | null, name: string, val: string): void {
  if (el) el.setAttribute(name, val);
}

/** Mutate the DOM document to reflect a snapshot. Tolerates missing elements (NFR-TS-013). */
export function renderTopology(doc: Document, snap: ParsedSnapshot, opts: RenderOptions = {}): void {
  const t = opts.thresholds ?? DEFAULT_THRESHOLDS;
  const q = (sel: string): HTMLElement | null => doc.querySelector<HTMLElement>(sel);

  // Engine node
  const engine = q('[data-node="engine"]');
  setText(engine, 'concurrency', String(snap.totalConcurrency));
  setText(engine, 'tps', `${Math.round(snap.aggregateTps * 10) / 10}`);
  setText(engine, 'breaches', String(snap.invariantBreaches));

  // Bank nodes + edges
  for (const b of snap.banks) {
    const node = q(`[data-bank="${b.bank}"]`);
    const hd = healthDisplay(b.health);
    const bd = breakerDisplay(b.breaker);
    setAttr(node, 'data-health', hd.cls);
    setAttr(node, 'data-breaker', bd.cls);
    setText(node, 'health', hd.label);
    setText(node, 'health-icon', hd.icon);
    setText(node, 'breaker', bd.label);
    setText(node, 'concurrency', String(b.concurrency));
    setText(node, 'rps', `${Math.round(b.throughputRps * 10) / 10}`);
    setText(node, 'error', `${Math.round(b.errorRate * 1000) / 10}%`);
    setText(node, 'p95', `${Math.round(b.latencyP95 * 1000)}ms`);
    setText(node, 'limit', `${b.envelope.maxConcurrency}c / ${b.envelope.maxRate}r`);

    const edge = q(`[data-edge="${b.bank}"]`);
    const es = edgeStyle(b.throughputRps, b.errorRate, t);
    setAttr(edge, 'stroke-width', String(es.width));
    setAttr(edge, 'data-band', es.band);
    setAttr(edge, 'stroke-dasharray', es.dashed ? '7 5' : 'none');

    // Animated packets travelling the edge = visible flow; faster cadence with more throughput.
    const flow = q(`[data-flow="${b.bank}"]`);
    if (flow) {
      const active = !snap.idle && b.throughputRps > 0.2;
      flow.setAttribute('data-active', active ? 'true' : 'false');
      flow.setAttribute('data-band', es.band);
      const dur = Math.max(0.5, Math.min(6, 9 / (1 + b.throughputRps)));
      flow.style.setProperty('--flow-dur', `${Math.round(dur * 100) / 100}s`);
      flow.querySelectorAll('animateMotion').forEach((a) => a.setAttribute('dur', `${Math.round(dur * 100) / 100}s`));
    }
  }

  // Scenario lanes: aggregate per class across banks (count of present outcomes as activity proxy)
  const perClass = new Map<string, number>();
  for (const b of snap.banks) {
    for (const key of b.requests.keys()) {
      const sc = key.slice(0, key.indexOf(':'));
      if (sc) perClass.set(sc, (perClass.get(sc) ?? 0) + 1);
    }
  }
  const lanes = doc.querySelectorAll('[data-lane]');
  lanes.forEach((lane) => {
    const sc = lane.getAttribute('data-lane') ?? '';
    const active = perClass.get(sc) ?? 0;
    setAttr(lane, 'data-active', active > 0 ? 'true' : 'false');
    setText(lane, 'count', String(active));
  });

  // Idle + staleness containers
  setAttr(q('[data-topology]'), 'data-idle', snap.idle ? 'true' : 'false');
  const idleBanner = q('[data-idle-banner]');
  if (idleBanner) idleBanner.textContent = snap.idle ? 'IDLE — no run active' : '';
}
